import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { blogPosts, getPostBySlug } from "@/data/blogPosts";

const BOOKING_URL = "https://booking.wellnessacademyusa.com/booking-page-wellness-academy";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) return <Navigate to="/blog" replace />;

  const related = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <main>
      <SeoHead
        title={`${post.title} | Wellness Academy Blog`}
        description={post.excerpt}
        image={post.image}
        path={`/blog/${post.slug}`}
      />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${post.image})` }}
        />
        <div className="absolute inset-0 bg-black/55" />
        <div
          className="relative z-10 max-w-4xl mx-auto w-full px-6 sm:px-8 md:px-[60px] py-32 flex flex-col items-center text-center"
        >
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-white/80 hover:text-nav-cta mb-8 transition-colors"
          >
            <ArrowLeft size={14} />
            Back to the Blog
          </Link>
          <p className="font-sans text-xs uppercase tracking-[0.3em] text-nav-cta mb-5">
            {post.category}
          </p>
          <h1 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-[1.1] tracking-wide mb-8">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-sans text-sm text-white/80">
            <span>By {post.author}</span>
            <span className="w-1 h-1 rounded-full bg-white/40" />
            <span>{post.date}</span>
            <span className="w-1 h-1 rounded-full bg-white/40" />
            <span>{post.readTime}</span>
          </div>
        </div>
      </section>

      <div className="border-t border-nav-cta/25" />

      {/* Body */}
      <article className="bg-background py-20 md:py-[100px]">
        <div
          className="max-w-3xl mx-auto px-6 sm:px-8"
        >
          <p className="font-serif italic text-xl md:text-2xl text-foreground leading-relaxed mb-12 pb-12 border-b border-foreground/15">
            {post.excerpt}
          </p>
          <div className="blog-content space-y-7">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => (
                  <h2 className="font-serif font-light text-2xl md:text-3xl text-foreground leading-tight mb-4 mt-8 tracking-wide">
                    {children}
                  </h2>
                ),
                h2: ({ children }) => (
                  <h2 className="font-serif font-light text-2xl md:text-3xl text-foreground leading-tight mb-4 mt-8 tracking-wide">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="font-serif font-light text-xl md:text-2xl text-foreground leading-tight mb-3 mt-6 tracking-wide">
                    {children}
                  </h3>
                ),
                p: ({ children }) => (
                  <p
                    className="text-base md:text-lg text-foreground leading-[1.85]"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {children}
                  </p>
                ),
                ul: ({ children }) => (
                  <ul
                    className="list-disc pl-6 space-y-2 text-base md:text-lg text-foreground leading-[1.85]"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol
                    className="list-decimal pl-6 space-y-2 text-base md:text-lg text-foreground leading-[1.85]"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {children}
                  </ol>
                ),
                a: ({ href, children }) => (
                  <a
                    href={href}
                    className="text-nav-cta underline underline-offset-4 hover:opacity-80 transition-opacity"
                    target={href?.startsWith("http") ? "_blank" : undefined}
                    rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
                  >
                    {children}
                  </a>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold text-foreground">{children}</strong>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-2 border-nav-cta pl-6 italic font-serif text-xl text-foreground my-8">
                    {children}
                  </blockquote>
                ),
                img: ({ src, alt }) => (
                  <img
                    src={src}
                    alt={alt ?? ""}
                    loading="lazy"
                    className="w-full h-auto my-10"
                  />
                ),
              }}
            >
              {post.content.join("\n\n")}
            </ReactMarkdown>
          </div>

        </div>
      </article>

      {/* CTA */}
      <section className="bg-secondary py-20 md:py-[100px]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
          <p className="font-sans text-xs uppercase tracking-[0.3em] text-nav-cta mb-5">
            Ready to Begin?
          </p>
          <h2 className="font-serif font-light text-3xl md:text-4xl text-foreground leading-tight mb-6 tracking-wide">
            Take the First Step Toward Lasting Health
          </h2>
          <p className="font-sans text-base md:text-lg text-foreground/80 leading-relaxed mb-8">
            Schedule a complimentary consultation to discuss your goals with our team.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button variant="hero" size="lg" className="px-10 py-6">
              Book a Consultation
            </Button>
          </a>
        </div>
      </section>

      <div className="border-t border-foreground/10" />

      {/* Related */}
      <section className="bg-background py-20 md:py-[100px]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-[60px]">
          <div className="flex items-baseline justify-between mb-12">
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-nav-cta">
              Continue Reading
            </p>
            <Link
              to="/blog"
              className="hidden md:inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-foreground/70 hover:text-nav-cta transition-colors"
            >
              All Articles
              <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
            {related.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group flex flex-col">
                <div className="relative overflow-hidden aspect-[4/5] mb-6">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                    width={1280}
                    height={896}
                  />
                </div>
                <p className="font-sans text-xs uppercase tracking-[0.2em] text-nav-cta mb-3">
                  {p.category}
                </p>
                <h3 className="font-serif font-light text-2xl text-foreground leading-tight group-hover:text-nav-cta transition-colors duration-500 tracking-wide">
                  {p.title}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default BlogPost;
